import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  type AuthAppContextInterface,
  type AuthAppContextState,
} from "components/root/auth-app-provider";
import {
  usePoliciesList,
  usePolicyDetail,
  usePolicyId,
  useUser,
} from "data/ss-data-hooks";
import {
  type KeystonePolicyInfo,
  logException,
  setMixpanelProfileProperties,
  setMixpanelSuperProperties,
} from "utils";

export const useAuthAppData = (
  state: AuthAppContextState,
  {
    setPolicies,
    setUserBilling,
    setUserDetails,
    setUserDocuments,
    setUserInfo,
    setUserPolicyProperties,
    setUserInsuranceRep,
    setPolicy,
  }: Omit<AuthAppContextInterface, "refetchPolicy" | "refetchPolicies">,
  setIsPolicyDataReady: (isPolicyDataReady: boolean) => void
) => {
  const selectedPolicyId = usePolicyId();
  const user = useUser();
  const {
    data: policies,
    isLoading: policiesLoading,
    mutate: mutatePolicies,
  } = usePoliciesList();
  const ssPolicyId =
    policies?.find((p) => p.keystonePolicyId === selectedPolicyId)
      ?.platformPolicyId ?? "";

  const { data: policyDetail, mutate } = usePolicyDetail();
  const refreshPolicyDetail = useCallback(() => {
    mutate();
  }, [mutate]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [commonPolicyDataIsDone, setCommonPolicyIsDone] = useState(false);

  // policyFetchCount is used purely to trigger a useEffect below every time the policy changes
  // Without it, switching to a cached policy doesn't trigger it we get stuck in loading
  const [policyFetchCount, setPolicyFetchCount] = useState(0);

  // throwing an error inside these "do<query>Query" functions doesn't get caught by the error boundaries
  // So, we set this state and throw here
  const [asyncError, setAsyncError] = useState<Error | null>(null);
  useEffect(() => {
    if (asyncError) {
      logException(asyncError);
      throw asyncError;
    }
  }, [asyncError]);

  const doCommonPolicyDataQuery = useCallback(async () => {
    setCommonPolicyIsDone(false);
    setUserBilling(policyDetail?.userBilling);
    setUserDetails(policyDetail?.userDetails);
    setUserDocuments(policyDetail?.userDocuments);
    setUserInfo({
      email: user?.email,
      firstName: user?.firstName,
      lastName: user?.lastName,
      userId: user?.userId,
    });
    setUserPolicyProperties(policyDetail?.userPolicyProperties);
    setUserInsuranceRep(policyDetail?.userInsuranceRep);
    setCommonPolicyIsDone(true);
  }, [
    setUserBilling,
    setUserDetails,
    setUserDocuments,
    setUserInfo,
    setUserPolicyProperties,
    setUserInsuranceRep,
    policyDetail,
    user,
  ]);

  useEffect(() => {
    if (!ssPolicyId) {
      return;
    }
    setPolicyFetchCount((prev) => prev + 1);
    doCommonPolicyDataQuery();
  }, [doCommonPolicyDataQuery, ssPolicyId]);

  const checkForPolicyInSearchParams = useCallback(
    (policies = state.policies) => {
      if (searchParams.get("policyNumber") || searchParams.get("ipId")) {
        const validPolicy = policies?.find(
          (userPolicy) =>
            userPolicy.policyId === searchParams.get("policyNumber") ||
            userPolicy.insightPolicyId === searchParams.get("ipId")
        );

        if (validPolicy) {
          setPolicy(validPolicy);
          // we only remove the qs param if it's valid so that error messages can use it
          // and because this can fire before state.policies is populated which is a "false invalid"
          setSearchParams((params) => {
            params.delete("policyNumber");
            params.delete("ipId");
            return params;
          });
          return true;
        }
      }
      return false;
    },
    [searchParams, setPolicy, setSearchParams, state.policies]
  );

  useEffect(() => {
    checkForPolicyInSearchParams();
  }, [checkForPolicyInSearchParams]);

  const doUserPolicyIDsQuery = useCallback(async () => {
    if (policiesLoading) {
      return;
    }

    if (!policies?.length) {
      setAsyncError(new Error("No policyIDs returned from ui-server"));
      return;
    }

    const mappedPolicies: KeystonePolicyInfo[] = policies.map((p) => ({
      policyId: p.platformPolicyId,
      insightPolicyId: p.keystonePolicyId,
      isCommercial: p.isCommercial ?? false,
      policyState: p.policyState,
      propertyAddress: p.propertyAddress,
      policyType: p.policyType ?? null,
    }));

    const policy = mappedPolicies.find(
      (p) => p.insightPolicyId === selectedPolicyId
    );

    if (!policy) {
      setAsyncError(new Error("No policy found"));
      return;
    }

    setPolicies(mappedPolicies);
    setMixpanelSuperProperties({
      numberOfPolicies: mappedPolicies.length,
    });
    setMixpanelProfileProperties({
      numberOfPolicies: mappedPolicies.length,
    });

    setPolicy(policy);
  }, [policiesLoading, policies, setPolicies, setPolicy, selectedPolicyId]);

  const refetchPolicy = useCallback(() => {
    refreshPolicyDetail();
    doCommonPolicyDataQuery();
  }, [refreshPolicyDetail, doCommonPolicyDataQuery]);

  const refetchPolicies = useCallback(() => {
    mutatePolicies();
    doUserPolicyIDsQuery();
  }, [mutatePolicies, doUserPolicyIDsQuery]);

  useEffect(() => {
    doUserPolicyIDsQuery();
  }, [doUserPolicyIDsQuery]);

  useEffect(() => {
    if (!policyFetchCount) {
      return;
    }
    setIsPolicyDataReady(commonPolicyDataIsDone);
  }, [commonPolicyDataIsDone, setIsPolicyDataReady, policyFetchCount]);

  return { refetchPolicy, refetchPolicies };
};
